import {
  ComponentType,
  createContext,
  DependencyList,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect
} from 'react'
import { LinearTransform } from '@st/geom'

// Use layout effect in browser, but fall back to effect during SSR/testing to avoid warnings
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const ParentRenderBoxContext = createContext<RenderBox | undefined>(undefined)
const RequestLayoutContext = createContext<() => void>(() => {})

export const RenderBoxProvider = ParentRenderBoxContext.Provider
export const RequestLayoutProvider = RequestLayoutContext.Provider

export type BoxConstraints = {
  minWidth: number
  maxWidth: number
  minHeight: number
  maxHeight: number
}

export const BoxConstraints = {
  /** Returns the intersection of two constraint rectangles, similar to
   *  Flutter's BoxConstraints.enforce().
   *
   *  The result guarantees:
   *    min ≤ max on each axis.
   *    The tightest (most restrictive) bounds win.
   */
  enforce: (parent: BoxConstraints, self: BoxConstraints): BoxConstraints => {
    const minWidth = Math.max(parent.minWidth, self.minWidth)
    const maxWidth = Math.min(parent.maxWidth, self.maxWidth)
    const minHeight = Math.max(parent.minHeight, self.minHeight)
    const maxHeight = Math.min(parent.maxHeight, self.maxHeight)

    return {
      minWidth: Math.min(minWidth, maxWidth),
      maxWidth: Math.max(minWidth, maxWidth),
      minHeight: Math.min(minHeight, maxHeight),
      maxHeight: Math.max(minHeight, maxHeight)
    }
  },

  tight: (size: Size): BoxConstraints => {
    return {
      minWidth: size.width,
      maxWidth: size.width,
      minHeight: size.height,
      maxHeight: size.height
    }
  },

  /**
   * Returns a constraint with the minimum dimensions set to 0,
   * while preserving the original maximum dimensions.
   *
   * This is equivalent to Flutter's BoxConstraints.loosen() method.
   * It's used when a parent wants to let a child be as small as it wants.
   */
  loosen: (constraints: BoxConstraints): BoxConstraints => {
    return {
      minWidth: 0,
      maxWidth: constraints.maxWidth,
      minHeight: 0,
      maxHeight: constraints.maxHeight
    }
  },

  /**
   * Clamps a raw size so it satisfies this constraint rectangle.
   * Same semantics as Flutter's BoxConstraints.constrain().
   */
  constrain: (c: BoxConstraints, size: { width: number; height: number }) => ({
    width: Math.min(Math.max(size.width, c.minWidth), c.maxWidth),
    height: Math.min(Math.max(size.height, c.minHeight), c.maxHeight)
  }),

  /**
   * Returns a new constraint rectangle deflated by the given padding.
   * Same semantics as Flutter's BoxConstraints.deflate().
   */
  deflate: (c: BoxConstraints, p: EdgeInsets): BoxConstraints => ({
    minWidth: Math.max(0, c.minWidth - p.left - p.right),
    maxWidth: Math.max(0, c.maxWidth - p.left - p.right),
    minHeight: Math.max(0, c.minHeight - p.top - p.bottom),
    maxHeight: Math.max(0, c.maxHeight - p.top - p.bottom)
  })
}

export type Font = {
  family: string
  weight: 'normal' | 'bold'
  size: number
}

export type Size = {
  width: number
  height: number
}

export type Rect = {
  x: number
  y: number
  width: number
  height: number
}

export type Point = {
  x: number
  y: number
}

export type Border = {
  color: string
  width: number
  radius?: number
}

export type EdgeInsets = { left: number; top: number; right: number; bottom: number }

export type TextAlign = 'left' | 'center' | 'right'

export const Pad = {
  horizontal: (n: number) => ({ left: n, right: n, top: 0, bottom: 0 }),
  vertical: (n: number) => ({ left: 0, right: 0, top: n, bottom: n }),
  symmetric: (opts: { horizontal?: number; vertical?: number }): EdgeInsets => {
    return {
      left: opts.horizontal ?? 0,
      top: opts.vertical ?? 0,
      right: opts.horizontal ?? 0,
      bottom: opts.vertical ?? 0
    }
  }
}

export type SurfaceProps = {
  width: number
  height: number
  drawOp: DrawOp
  debug?: boolean
  onTrace?: (e: RenderTraceEvent) => void
}

export type Surface = ComponentType<SurfaceProps>

export type RenderTraceEvent = { type: 'renderStep'; step: string; data: any }

export type DrawOp =
  | {
      type: 'group'
      id?: string
      bounds: Rect
      children: DrawOp[]
    }
  | {
      type: 'text'
      id?: string
      bounds: Rect
      text: string
      font: Font
      color: string
      line_height: number
      text_align: TextAlign
      kerning: false
    }
  | { type: 'rect'; id?: string; bounds: Rect; border?: Border; background_color?: string }
  | {
      type: 'path'
      id?: string
      bounds: Rect
      path: string
      fill?: string
      stroke?: string
      stroke_width?: number
      transform?: LinearTransform
    }

export function walkDrawOps(
  ops: DrawOp[],
  origin: Point,
  visitor: (op: DrawOp, origin: Point) => void
) {
  for (const op of ops) {
    visitor(op, origin)
    if (op.type === 'group') {
      walkDrawOps(op.children, { x: origin.x + op.bounds.x, y: origin.y + op.bounds.y }, visitor)
    }
  }
}

export abstract class RenderBox<Prop = any> {
  static _seed = 0
  readonly id = `rb${RenderBox._seed++}`

  children: RenderBox[] = []
  box: Rect = { x: 0, y: 0, width: 0, height: 0 }

  constructor(public props: Prop) {}
  add(child: RenderBox) {
    this.children.push(child)
  }

  abstract performLayout(c: BoxConstraints): void

  draw(): DrawOp {
    return {
      type: 'group',
      id: this.id,
      bounds: this.box,
      children: this.children.map((c) => c.draw())
    }
  }

  // ---- Child ordering helpers ----
  private _nextChildIndex = 0

  /** Reset counter (call at the start of parent's render pass) */
  resetChildIndex() {
    this._nextChildIndex = 0
  }

  /** Return current index and increment */
  nextChildIndex(): number {
    return this._nextChildIndex++
  }

  /** Insert child at idx, moving it if it already exists */
  insertAt(child: RenderBox, idx: number) {
    const cur = this.children.indexOf(child)
    if (cur === idx) return
    if (cur !== -1) this.children.splice(cur, 1)
    if (idx >= 0 && idx <= this.children.length) {
      this.children.splice(idx, 0, child)
    } else {
      this.children.push(child)
    }
  }

  removeChild(child: RenderBox) {
    const i = this.children.indexOf(child)
    if (i !== -1) this.children.splice(i, 1)
  }
}

export function useRenderBox<T extends RenderBox>(factory: () => T, deps: DependencyList): T {
  const parent = useContext(ParentRenderBoxContext)!
  const requestLayout = useContext(RequestLayoutContext)

  // Stable imperative instance
  const box = useMemo(factory, deps)
  const index = parent.nextChildIndex()

  // During the very first mount attach synchronously so that the initial
  // render pass sees this child.
  const hasInsertedSync = useRef(false)
  if (!hasInsertedSync.current) {
    parent.insertAt(box, index)
    hasInsertedSync.current = true
  }

  // After commit: handle re-order / prop changes and clean-up
  useIsomorphicLayoutEffect(() => {
    parent.insertAt(box, index) // move if index changed
    requestLayout()
    return () => {
      parent.removeChild(box)
      requestLayout()
    }
  }, [box, parent, index, requestLayout])

  return box
}
